import React, { useContext } from "react";
import { Card } from "uikit/components";
import { number, shape, string } from "prop-types";
import cx from "classnames";
import styles from "./BuildingLoadingSummaryCard.module.styl";
import { percentageLevels } from "utils/indicator/indicatorSettings";
import Chart from "react-apexcharts";
import { chartOptions, chartWidth, smallChartWidth } from "utils/chart/chartOptions";
import { MediaContext } from "app/media/MediaContext";
import ValueHeaderCard from "components/dashboard/ValueHeaderCard/ValueHeaderCard";
import IconTooltip from "uikit/components/IconTooltip/IconTooltip";

export default function BuildingLoadingSummaryCard({ buildingData }) {
    const { isMobile, isTablet, isDesktop } = useContext(MediaContext);

    const indicatorStyle = percentageLevels.indicatorColorWithOverload(buildingData.loadingPercent);

    const options = chartOptions(indicatorStyle, isTablet ? "16px" : "24px", true);

    return isMobile ? (
        <ValueHeaderCard
            headerPercent={buildingData.loadingPercent}
            headerText={indicatorStyle.loadingText}
        >
            <div className={styles.valueHeaderCardContainer}>
                <div>
                    <div className={styles.indicator}>{buildingData.sportTypesCount}</div>
                    <div className={styles.valueHeaderCardText}>видов спорта</div>
                </div>
                <div>
                    <div className={styles.indicator}>{buildingData.throughput}</div>
                    <div className={styles.valueHeaderCardText}>
                        суммарная пропускная способность, человек
                    </div>
                </div>
                <div>
                    <div className={styles.indicator}>{buildingData.workingDaysCount}</div>
                    <div className={styles.valueHeaderCardText}>
                        дней в неделю объект задействован
                    </div>
                </div>
                <div>
                    <div className={styles.indicator}>{buildingData.sportObjectPlaceCount}</div>
                    <div className={styles.valueHeaderCardText}>мест в составе объекта</div>
                </div>
                <div>
                    <div className={styles.indicator}>{buildingData.maxDailyCapacity}</div>
                    <div className={styles.valueHeaderCardText}>
                        максимальная дневная мощность объекта спорта
                    </div>
                </div>
                <div>
                    <div className={styles.indicator}>
                        {buildingData.sportObjectParticipantCount}
                    </div>
                    <div className={styles.valueHeaderCardText}>посетителей объекта за неделю</div>
                </div>
                <div className={styles.valueHeaderCardText}>
                    <div className={styles.boldSchoolName}>Собственник объекта спорта:</div>
                    <div>{buildingData.schoolName ?? "Не указан"}</div>
                </div>
            </div>
        </ValueHeaderCard>
    ) : (
        <Card
            borderRadius={"giant"}
            padding={"none"}
            className={cx(styles.form, indicatorStyle.style)}
        >
            <div className={styles.content} data-test-name="building_loading_summary_card">
                <div>
                    <div className={styles.indicators}>
                        <div>
                            <div className={styles.indicator}>
                                <div>{buildingData.sportTypesCount}</div>
                            </div>
                            <div className={styles.title}>видов спорта</div>
                        </div>
                        <div>
                            <div className={styles.indicator}>
                                <div>{buildingData.throughput}</div>
                            </div>
                            <div className={styles.title}>
                                суммарная пропускная способность, человек
                            </div>
                        </div>
                        <div>
                            <div className={styles.indicator}>
                                <div>{buildingData.workingDaysCount}</div>
                            </div>
                            <div className={styles.title}>дней в неделю объект задействован</div>
                        </div>
                        <div>
                            <div className={styles.indicator}>
                                <div>{buildingData.sportObjectPlaceCount}</div>
                            </div>
                            <div className={styles.title}>мест в составе объекта</div>
                        </div>
                        <div>
                            <div className={styles.indicator}>
                                <div>{buildingData.maxDailyCapacity}</div>
                            </div>
                            <div className={styles.title}>
                                максимальная дневная мощность объекта спорта
                            </div>
                        </div>
                        <div>
                            <div className={styles.indicator}>
                                <div>{buildingData.sportObjectParticipantCount}</div>
                            </div>
                            <div className={styles.title}>посетителей объекта за неделю</div>
                        </div>
                        <div className={styles.chart}>
                            <Chart
                                options={options}
                                series={[buildingData.loadingPercent]}
                                type="radialBar"
                                width={isDesktop ? chartWidth : smallChartWidth}
                            />
                            <div className={styles.percentageText}>
                                {indicatorStyle.loadingText}
                                <IconTooltip trigger="hover">
                                    <div className={styles.infoPanel}>
                                        Загрузка объекта спорта - среднее арифметическое по
                                        значениям загрузки за неделю всех мест объекта спорта.
                                        Учитывается загрузка, создаваемая только спортивными
                                        организациями.
                                    </div>
                                </IconTooltip>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.boldSchoolName}>
                    Собственник объекта спорта:{" "}
                    <div className={styles.schoolName}>
                        {buildingData.schoolName ?? "Не указан"}
                    </div>
                </div>
            </div>
        </Card>
    );
}

BuildingLoadingSummaryCard.propTypes = {
    buildingData: shape({
        id: number.isRequired,
        name: string.isRequired,
        schoolName: string.isRequired,
        sportTypesCount: number.isRequired,
        throughput: number.isRequired,
        workingDaysCount: number.isRequired,
        maxDailyCapacity: number.isRequired,
        loadingPercent: number.isRequired,
        municipalityName: string.isRequired,
        sportObjectPlaceCount: number.isRequired,
        sportObjectParticipantCount: number.isRequired,
    }),
};
